import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  Check,
  Clipboard,
  Edit3,
  Eye,
  EyeOff,
  Globe2,
  Link2,
  Lock,
  Plus,
  Search,
  Share2,
  Trash2,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api, ApiError } from '../api';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';

interface Share {
  id: string;
  subjectType: 'USER' | 'GROUP';
  role: 'READ' | 'WRITE';
  user?: { id: string; email: string; name?: string | null } | null;
  group?: { id: string; name: string } | null;
}

interface PublicLink {
  id: string;
  token: string;
  label: string;
  enabled: boolean;
  includeDetails: boolean;
  createdAt: string;
  lastUsedAt?: string | null;
}

interface Calendar {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  color: string;
  timezone: string;
  enabled: boolean;
  ctag: number;
  accessRole: 'OWNER' | 'WRITE' | 'READ';
  owner: { id: string; email: string; name?: string | null };
  shares: Share[];
  publicLinks: PublicLink[];
  _count?: { events: number; shares: number; publicLinks: number };
}

interface ShareTargets {
  users: Array<{ id: string; email: string; name?: string | null }>;
  groups: Array<{ id: string; name: string }>;
}

const colorPresets = ['#8B5CF6', '#EC4899', '#14B8A6', '#3B82F6', '#F59E0B', '#22C55E', '#EF4444'];

export function CalendarsPage() {
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [targets, setTargets] = useState<ShareTargets>({ users: [], groups: [] });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState({ slug: '', name: '', color: '#8B5CF6', timezone: 'Europe/Berlin', description: '' });
  const [shareForm, setShareForm] = useState({ subjectType: 'USER' as 'USER' | 'GROUP', userId: '', groupId: '', role: 'READ' as 'READ' | 'WRITE' });
  const [linkForm, setLinkForm] = useState({ label: 'Oeffentlicher Kalender', includeDetails: true });
  const toast = useToast();

  const load = async () => {
    const [cals, targetData] = await Promise.all([
      api.get<Calendar[]>('/api/calendars'),
      api.get<ShareTargets>('/api/share-targets'),
    ]);
    setCalendars(cals);
    setTargets(targetData);
    setSelectedId((current) => current && cals.some((cal) => cal.id === current) ? current : cals[0]?.id ?? null);
  };

  useEffect(() => {
    load();
  }, []);

  const filteredCalendars = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return calendars;
    return calendars.filter((cal) =>
      cal.name.toLowerCase().includes(q) ||
      cal.slug.toLowerCase().includes(q) ||
      cal.owner.email.toLowerCase().includes(q) ||
      cal.description?.toLowerCase().includes(q),
    );
  }, [calendars, query]);

  const selected = calendars.find((cal) => cal.id === selectedId) ?? filteredCalendars[0] ?? null;
  const ownerCalendars = calendars.filter((cal) => cal.accessRole === 'OWNER');
  const sharedCalendars = calendars.filter((cal) => cal.accessRole !== 'OWNER');
  const publicLinks = calendars.reduce((sum, cal) => sum + cal.publicLinks.length, 0);

  const resetForm = () => setForm({ slug: '', name: '', color: '#8B5CF6', timezone: 'Europe/Berlin', description: '' });

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const openEdit = (cal: Calendar) => {
    setForm({
      slug: cal.slug,
      name: cal.name,
      color: cal.color,
      timezone: cal.timezone,
      description: cal.description ?? '',
    });
    setEditOpen(true);
  };

  const handleCreate = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      const created = await api.post<Calendar>('/api/calendars', form);
      toast.success('Kalender angelegt');
      setCreateOpen(false);
      resetForm();
      await load();
      setSelectedId(created.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Kalender konnte nicht angelegt werden');
    }
  };

  const saveSelected = async () => {
    if (!selected) return;
    try {
      await api.patch(`/api/calendars/${selected.id}`, {
        name: form.name,
        description: form.description || null,
        color: form.color,
        timezone: form.timezone,
      });
      toast.success('Kalender gespeichert');
      setEditOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Kalender konnte nicht gespeichert werden');
    }
  };

  const toggle = async (cal: Calendar) => {
    await api.patch(`/api/calendars/${cal.id}`, { enabled: !cal.enabled });
    toast.success(cal.enabled ? 'Kalender deaktiviert' : 'Kalender aktiviert');
    await load();
  };

  const remove = async (cal: Calendar) => {
    await api.delete(`/api/calendars/${cal.id}`);
    toast.success(`Kalender "${cal.name}" geloescht`);
    await load();
  };

  const addShare = async () => {
    if (!selected) return;
    try {
      await api.post(`/api/calendars/${selected.id}/shares`, shareForm);
      toast.success('Freigabe gespeichert');
      setShareForm({ subjectType: 'USER', userId: '', groupId: '', role: 'READ' });
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Freigabe konnte nicht gespeichert werden');
    }
  };

  const removeShare = async (share: Share) => {
    if (!selected) return;
    await api.delete(`/api/calendars/${selected.id}/shares/${share.id}`);
    toast.success('Freigabe entfernt');
    await load();
  };

  const createLink = async () => {
    if (!selected) return;
    try {
      await api.post(`/api/calendars/${selected.id}/public-links`, linkForm);
      toast.success('ICS-Link erstellt');
      setLinkForm({ label: 'Oeffentlicher Kalender', includeDetails: true });
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ICS-Link konnte nicht erstellt werden');
    }
  };

  const toggleLink = async (link: PublicLink) => {
    if (!selected) return;
    await api.patch(`/api/calendars/${selected.id}/public-links/${link.id}`, { enabled: !link.enabled });
    await load();
  };

  const toggleLinkDetails = async (link: PublicLink) => {
    if (!selected) return;
    await api.patch(`/api/calendars/${selected.id}/public-links/${link.id}`, { includeDetails: !link.includeDetails });
    await load();
  };

  const deleteLink = async (link: PublicLink) => {
    if (!selected) return;
    await api.delete(`/api/calendars/${selected.id}/public-links/${link.id}`);
    toast.success('ICS-Link entfernt');
    await load();
  };

  const publicUrl = (token: string) => `${window.location.origin}/public/ics/${token}`;

  const copyUrl = async (token: string) => {
    const url = publicUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link kopiert');
    } catch {
      toast.info(url, 'ICS-Link');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="overflow-hidden rounded-xl border border-border bg-[linear-gradient(180deg,rgba(255,255,255,0.075),rgba(255,255,255,0.025))] shadow-card">
        <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-md border border-border bg-bg/45 px-3 py-1.5 text-small text-ink-secondary">
              <CalendarDays size={15} />Kalenderverwaltung
            </div>
            <h1 className="text-h2">Kalender, Freigaben & ICS</h1>
            <div className="mt-2 text-body text-ink-secondary">Besitz, Gruppenfreigaben und oeffentliche Abos an einem Ort.</div>
          </div>
          <button className="btn btn-primary" onClick={openCreate}><Plus size={17} />Neuer Kalender</button>
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-border/70 bg-bg/20 p-3 sm:grid-cols-4 sm:p-4">
          <Metric icon={CalendarDays} label="Gesamt" value={String(calendars.length)} />
          <Metric icon={Lock} label="Eigene" value={String(ownerCalendars.length)} />
          <Metric icon={Share2} label="Geteilt" value={String(sharedCalendars.length)} />
          <Metric icon={Globe2} label="ICS-Links" value={String(publicLinks)} />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(320px,420px),1fr]">
        <section className="rounded-xl border border-border bg-surface-card p-3 shadow-card sm:p-4">
          <label className="relative mb-3 block">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input className="input pl-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Kalender, Owner, Slug suchen" />
          </label>
          <div className="grid gap-2">
            {filteredCalendars.map((cal) => (
              <button
                key={cal.id}
                type="button"
                onClick={() => setSelectedId(cal.id)}
                className={`rounded-lg border p-3 text-left transition-colors ${selected?.id === cal.id ? 'border-brand-primary/55 bg-brand-primary/12' : 'border-border bg-surface-elevated hover:bg-white/[0.045]'}`}
              >
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{ background: cal.color }} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-semibold text-ink-primary">{cal.name}</span>
                      <Badge tone={cal.enabled ? 'success' : 'muted'}>{cal.enabled ? 'aktiv' : 'aus'}</Badge>
                    </span>
                    <span className="mt-1 block truncate font-mono text-small text-ink-muted">{cal.slug} · {cal.owner.email}</span>
                  </span>
                  <Badge tone={cal.accessRole === 'OWNER' ? 'info' : cal.accessRole === 'WRITE' ? 'warning' : 'muted'}>{cal.accessRole}</Badge>
                </div>
              </button>
            ))}
            {filteredCalendars.length === 0 && <div className="rounded-md border border-dashed border-border p-4 text-small text-ink-muted">Keine Kalender gefunden.</div>}
          </div>
        </section>

        {selected ? (
          <CalendarWorkspace
            calendar={selected}
            targets={targets}
            shareForm={shareForm}
            setShareForm={setShareForm}
            linkForm={linkForm}
            setLinkForm={setLinkForm}
            publicUrl={publicUrl}
            onEdit={() => openEdit(selected)}
            onToggle={() => toggle(selected)}
            onDelete={() => remove(selected)}
            onAddShare={addShare}
            onRemoveShare={removeShare}
            onCreateLink={createLink}
            onToggleLink={toggleLink}
            onToggleLinkDetails={toggleLinkDetails}
            onDeleteLink={deleteLink}
            onCopyLink={copyUrl}
          />
        ) : (
          <section className="rounded-xl border border-dashed border-border bg-surface-card p-8 text-center text-ink-muted">Waehle einen Kalender aus.</section>
        )}
      </div>

      <CalendarFormModal
        open={createOpen}
        title="Neuer Kalender"
        form={form}
        setForm={setForm}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
      />

      <CalendarFormModal
        open={editOpen}
        title="Kalender bearbeiten"
        form={form}
        setForm={setForm}
        onClose={() => setEditOpen(false)}
        onSave={saveSelected}
        slugDisabled
      />
    </div>
  );
}

function CalendarWorkspace({
  calendar,
  targets,
  shareForm,
  setShareForm,
  linkForm,
  setLinkForm,
  publicUrl,
  onEdit,
  onToggle,
  onDelete,
  onAddShare,
  onRemoveShare,
  onCreateLink,
  onToggleLink,
  onToggleLinkDetails,
  onDeleteLink,
  onCopyLink,
}: {
  calendar: Calendar;
  targets: ShareTargets;
  shareForm: { subjectType: 'USER' | 'GROUP'; userId: string; groupId: string; role: 'READ' | 'WRITE' };
  setShareForm: (form: { subjectType: 'USER' | 'GROUP'; userId: string; groupId: string; role: 'READ' | 'WRITE' }) => void;
  linkForm: { label: string; includeDetails: boolean };
  setLinkForm: (form: { label: string; includeDetails: boolean }) => void;
  publicUrl: (token: string) => string;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onAddShare: () => void;
  onRemoveShare: (share: Share) => void;
  onCreateLink: () => void;
  onToggleLink: (link: PublicLink) => void;
  onToggleLinkDetails: (link: PublicLink) => void;
  onDeleteLink: (link: PublicLink) => void;
  onCopyLink: (token: string) => void;
}) {
  const editable = calendar.accessRole === 'OWNER';
  return (
    <section className="grid gap-4">
      <div className="rounded-xl border border-border bg-surface-card p-5 shadow-card">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-3">
              <span className="h-4 w-4 rounded-full" style={{ background: calendar.color }} />
              <h2 className="truncate text-h3">{calendar.name}</h2>
              <Badge tone={calendar.enabled ? 'success' : 'muted'}>{calendar.enabled ? 'aktiv' : 'inaktiv'}</Badge>
            </div>
            <div className="font-mono text-small text-ink-muted">{calendar.slug} · {calendar.timezone}</div>
            <div className="mt-2 text-body text-ink-secondary">{calendar.description || 'Keine Beschreibung hinterlegt.'}</div>
          </div>
          {editable && (
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-secondary" onClick={onEdit}><Edit3 size={16} />Bearbeiten</button>
              <button className="btn btn-secondary" onClick={onToggle}>{calendar.enabled ? <EyeOff size={16} /> : <Eye size={16} />}{calendar.enabled ? 'Deaktivieren' : 'Aktivieren'}</button>
              <button className="btn btn-danger" onClick={onDelete}><Trash2 size={16} />Loeschen</button>
            </div>
          )}
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Termine" value={String(calendar._count?.events ?? 0)} />
          <Stat label="Freigaben" value={String(calendar._count?.shares ?? calendar.shares.length)} />
          <Stat label="ICS-Links" value={String(calendar._count?.publicLinks ?? calendar.publicLinks.length)} />
          <Stat label="CTag" value={String(calendar.ctag)} mono />
        </div>
      </div>

      <div className="grid gap-4 2xl:grid-cols-2">
        <section className="rounded-xl border border-border bg-surface-card p-5 shadow-card">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="inline-flex items-center gap-2 text-h4"><Users size={18} />Benutzer & Gruppen</h3>
            <Badge tone={editable ? 'info' : 'muted'}>{editable ? 'Owner' : calendar.accessRole}</Badge>
          </div>
          {editable && (
            <div className="mb-4 grid gap-3 lg:grid-cols-[130px,1fr,140px,auto] lg:items-end">
              <div>
                <label className="label">Typ</label>
                <select className="select" value={shareForm.subjectType} onChange={(e) => setShareForm({ ...shareForm, subjectType: e.target.value as 'USER' | 'GROUP' })}>
                  <option value="USER">Benutzer</option>
                  <option value="GROUP">Gruppe</option>
                </select>
              </div>
              <div>
                <label className="label">Ziel</label>
                {shareForm.subjectType === 'USER' ? (
                  <select className="select" value={shareForm.userId} onChange={(e) => setShareForm({ ...shareForm, userId: e.target.value })}>
                    <option value="">Benutzer auswaehlen</option>
                    {targets.users.map((u) => <option key={u.id} value={u.id}>{u.name ? `${u.name} · ${u.email}` : u.email}</option>)}
                  </select>
                ) : (
                  <select className="select" value={shareForm.groupId} onChange={(e) => setShareForm({ ...shareForm, groupId: e.target.value })}>
                    <option value="">Gruppe auswaehlen</option>
                    {targets.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                )}
              </div>
              <div>
                <label className="label">Rechte</label>
                <select className="select" value={shareForm.role} onChange={(e) => setShareForm({ ...shareForm, role: e.target.value as 'READ' | 'WRITE' })}>
                  <option value="READ">Lesen</option>
                  <option value="WRITE">Schreiben</option>
                </select>
              </div>
              <button className="btn btn-secondary" onClick={onAddShare}><Share2 size={16} />Freigeben</button>
            </div>
          )}
          <div className="grid gap-2">
            {calendar.shares.map((share) => (
              <div key={share.id} className="flex flex-col gap-3 rounded-md border border-border bg-surface-elevated p-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{share.user?.name ?? share.group?.name ?? share.user?.email}</div>
                  <div className="truncate font-mono text-small text-ink-muted">{share.user?.email ?? share.subjectType}</div>
                </div>
                <Badge tone={share.subjectType === 'GROUP' ? 'info' : 'muted'}>{share.subjectType}</Badge>
                <Badge tone={share.role === 'WRITE' ? 'warning' : 'muted'}>{share.role}</Badge>
                {editable && <button className="btn btn-ghost !py-1.5" onClick={() => onRemoveShare(share)}>Entfernen</button>}
              </div>
            ))}
            {calendar.shares.length === 0 && <div className="rounded-md border border-dashed border-border p-4 text-small text-ink-muted">Keine Freigaben.</div>}
          </div>
        </section>

        <section className="rounded-xl border border-border bg-surface-card p-5 shadow-card">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="inline-flex items-center gap-2 text-h4"><Globe2 size={18} />Oeffentliche ICS-Links</h3>
            <Badge tone={calendar.publicLinks.some((link) => link.enabled) ? 'success' : 'muted'}>{calendar.publicLinks.filter((link) => link.enabled).length} aktiv</Badge>
          </div>
          {editable && (
            <div className="mb-4 grid gap-3 lg:grid-cols-[1fr,150px,auto] lg:items-end">
              <div>
                <label className="label">Label</label>
                <input className="input" value={linkForm.label} onChange={(e) => setLinkForm({ ...linkForm, label: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 pb-2 text-body">
                <input type="checkbox" checked={linkForm.includeDetails} onChange={(e) => setLinkForm({ ...linkForm, includeDetails: e.target.checked })} />
                Details
              </label>
              <button className="btn btn-secondary" onClick={onCreateLink}><Link2 size={16} />Erstellen</button>
            </div>
          )}
          <div className="grid gap-2">
            {calendar.publicLinks.map((link) => (
              <div key={link.id} className="grid gap-3 rounded-md border border-border bg-surface-elevated p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{link.label}</div>
                    <div className="text-small text-ink-muted">
                      {link.lastUsedAt ? `zuletzt ${new Date(link.lastUsedAt).toLocaleString('de-DE')}` : 'noch nicht abgerufen'}
                    </div>
                  </div>
                  <Badge tone={link.enabled ? 'success' : 'muted'}>{link.enabled ? 'aktiv' : 'aus'}</Badge>
                  <Badge tone={link.includeDetails ? 'info' : 'warning'}>{link.includeDetails ? 'Details' : 'frei/belegt'}</Badge>
                </div>
                <div className="grid gap-2 sm:grid-cols-[1fr,auto]">
                  <input className="input font-mono text-small" value={publicUrl(link.token)} readOnly onFocus={(e) => e.currentTarget.select()} />
                  <button className="btn btn-secondary" onClick={() => onCopyLink(link.token)}><Clipboard size={16} />Kopieren</button>
                </div>
                {editable && (
                  <div className="flex flex-wrap gap-2">
                    <button className="btn btn-ghost !py-1.5" onClick={() => onToggleLink(link)}>{link.enabled ? 'Deaktivieren' : 'Aktivieren'}</button>
                    <button className="btn btn-ghost !py-1.5" onClick={() => onToggleLinkDetails(link)}>{link.includeDetails ? 'Details aus' : 'Details an'}</button>
                    <button className="btn btn-danger !py-1.5" onClick={() => onDeleteLink(link)}>Loeschen</button>
                  </div>
                )}
              </div>
            ))}
            {calendar.publicLinks.length === 0 && <div className="rounded-md border border-dashed border-border p-4 text-small text-ink-muted">Keine oeffentlichen Links.</div>}
          </div>
        </section>
      </div>
    </section>
  );
}

function CalendarFormModal({
  open,
  title,
  form,
  setForm,
  onClose,
  onSave,
  slugDisabled = false,
}: {
  open: boolean;
  title: string;
  form: { slug: string; name: string; color: string; timezone: string; description: string };
  setForm: (form: { slug: string; name: string; color: string; timezone: string; description: string }) => void;
  onClose: () => void;
  onSave: (event?: React.FormEvent) => void;
  slugDisabled?: boolean;
}) {
  return (
    <Modal open={open} onClose={onClose} title={title} footer={
      <>
        <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary" onClick={() => onSave()}>Speichern</button>
      </>
    }>
      <form onSubmit={onSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="label">Name</label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div>
          <label className="label">Slug</label>
          <input className="input font-mono" value={form.slug} disabled={slugDisabled} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} required />
        </div>
        <div>
          <label className="label">Zeitzone</label>
          <input className="input" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Farbe</label>
          <div className="flex flex-wrap items-center gap-2">
            {colorPresets.map((color) => (
              <button
                key={color}
                type="button"
                className={`flex h-8 w-8 items-center justify-center rounded-md border ${form.color.toLowerCase() === color.toLowerCase() ? 'border-white/80' : 'border-border'}`}
                style={{ background: color }}
                onClick={() => setForm({ ...form, color })}
                title={color}
              >
                {form.color.toLowerCase() === color.toLowerCase() && <Check size={15} className="text-white drop-shadow" />}
              </button>
            ))}
            <input type="color" className="input h-9 w-14 p-1" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Beschreibung</label>
          <textarea className="textarea" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </form>
    </Modal>
  );
}

function Metric({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/80 bg-surface-elevated/65 px-3 py-2.5">
      <div className="mb-1 flex items-center gap-2 text-meta uppercase tracking-[0.14em] text-ink-muted">
        <Icon size={14} className="text-ink-secondary" />
        {label}
      </div>
      <div className="text-h4 text-ink-primary">{value}</div>
    </div>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-surface-elevated p-3">
      <div className="text-meta uppercase tracking-[0.16em] text-ink-muted">{label}</div>
      <div className={`mt-1 text-body ${mono ? 'font-mono' : 'font-semibold'}`}>{value}</div>
    </div>
  );
}
